/**
 * MobileTabBar
 *
 * Fixed bottom navigation bar for mobile guard-location redesign.
 * Features:
 * - Three tabs: Map, Guards, Activity
 * - Active tab indicator with top border
 * - Badge count on Activity tab for undismissed alerts
 * - Safe-area padding for notched phones
 *
 * Visual Design:
 * ┌──────────────┬──────────────┬──────────────┐
 * │   [Place]    │  [People]    │  [Bell] (3)  │
 * │    Map       │   Guards     │   Activity   │
 * └──────────────┴──────────────┴──────────────┘
 */

import React from 'react';
import PlaceIcon from '@mui/icons-material/Place';
import PeopleIcon from '@mui/icons-material/People';
import NotificationsIcon from '@mui/icons-material/Notifications';
import styles from './MobileTabBar.module.css';
import classNames from 'classnames';

// ============================================
// Types
// ============================================

export type MobileTab = 'map' | 'guards' | 'activity';

interface MobileTabBarProps {
  activeTab: MobileTab;
  onTabChange: (tab: MobileTab) => void;
  alertCount: number;
}

// ============================================
// Tab configuration
// ============================================

const TAB_CONFIG: { id: MobileTab; label: string; Icon: React.ElementType }[] = [
  { id: 'map', label: 'Map', Icon: PlaceIcon },
  { id: 'guards', label: 'Guards', Icon: PeopleIcon },
  { id: 'activity', label: 'Activity', Icon: NotificationsIcon },
];

// ============================================
// Component
// ============================================

const MobileTabBar: React.FC<MobileTabBarProps> = ({
  activeTab,
  onTabChange,
  alertCount,
}) => {
  return (
    <nav className={styles.tabBar} role="tablist" aria-label="Mobile navigation">
      {TAB_CONFIG.map(({ id, label, Icon }) => {
        const isActive = activeTab === id;
        const showBadge = id === 'activity' && alertCount > 0;

        return (
          <button
            key={id}
            role="tab"
            aria-selected={isActive}
            aria-label={
              showBadge ? `${label} (${alertCount} alerts)` : label
            }
            className={classNames(styles.tab, { [styles.active]: isActive })}
            onClick={() => onTabChange(id)}
          >
            <span className={styles.tabIcon}>
              <Icon fontSize="inherit" />
            </span>
            <span className={styles.tabLabel}>{label}</span>
            {showBadge && (
              <span className={styles.badge} aria-hidden="true">
                {alertCount > 99 ? '99+' : alertCount}
              </span>
            )}
          </button>
        );
      })}
    </nav>
  );
};

export default MobileTabBar;
